import { AppRoutesList } from '@qse/constants'
import { toast } from '@qse/ui'
import { FormikHelpers } from 'formik'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { authService, type LoginResult } from './auth-service'

export interface SetNewPasswordData {
  password: string
  confirmPassword: string
}

// Validation schema for set new password
export const setNewPasswordValidationSchema = Yup.object({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
})

export const useSetNewPassword = () => {
  const navigate = useNavigate()

  const handleSetNewPassword = async (
    values: SetNewPasswordData,
    {
      setSubmitting,
      setStatus,
      onSuccess,
      onTOTPSetupRequired,
      onTOTPCodeRequired,
    }: Partial<FormikHelpers<SetNewPasswordData>> & {
      onSuccess?: (user: LoginResult) => void
      onTOTPSetupRequired?: (totpSetup: any, isFirstLogin?: boolean) => void
      onTOTPCodeRequired?: () => void
    },
  ) => {
    try {
      const result = await authService.setNewPassword(values.password)

      toast.success('Password updated successfully')

      if (result.requiresTOTPSetup) {
        onTOTPSetupRequired?.(result.totpSetup, result.isFirstLogin)
        navigate(AppRoutesList.twoFA, {
          replace: true,
          state: {
            isFirstLogin: result.isFirstLogin,
            totpSetup: result.totpSetup,
          },
        })

        return
      }

      if (result.requiresTOTPCode) {
        onTOTPCodeRequired?.()
        navigate(AppRoutesList.twoFAVerify, {
          replace: true,
          state: { totpSetup: result.totpSetup },
        })

        return
      }

      // Successful password change
      if (result.user) {
        onSuccess?.(result)
        // Redirect based on domain
        navigate(AppRoutesList.login, { replace: true })
      }
    } catch (error: any) {
      authService.handleError(error, 'general', setStatus)
    } finally {
      setSubmitting?.(false)
    }
  }

  return {
    handleSetNewPassword,
    setNewPasswordValidationSchema,
  }
}
